import styled from '@emotion/styled';
import { Table, TableBody, TableCell, TableRow, Typography } from '@material-ui/core';
import { createElement, FC, Fragment, useContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { DiscountCodeDetails } from '../../../../entity/DiscountCode';
import ProductPricing from '../../../../entity/ProductPricing';
import { calcDiscountPoint } from '../../../../utility/calcDiscountPoint';
import { displayMessageShorten } from '../../../../utility/displayMessageShorten';
import { context as StateContext } from '../../../context/state';
import mq from '../../../styleUtility/mediaquery';

type PassedProps = {
  pointBalance: number;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  total?: number;
  className?: string;
};

type ContainerProps = {
  total: number;
  discountCode: DiscountCodeDetails | null;
  discountTotal: number;
  useDepositTotal: number;
  useDepositTotalPoint: number;
  subtotal: number;
  taxTotal: number;
  orderTotal: number;
};

type Props = PassedProps & ContainerProps;

const ContainerComponent: FC<PassedProps> = ({
  pointBalance,
  isPointRequired,
  productPricing,
  total: _total,
  className,
}) => {
  // ディスカウントコードの取得
  const state = useContext(StateContext);
  const discountCode = state.projectEditing.discountCode;

  const totalPoint = _total ? _total : 0;

  // ディスカウントの計算
  const discountTotalPoint = calcDiscountPoint(discountCode, totalPoint);
  let discountTotal = 0;
  if (productPricing && discountCode && discountCode.type === 'success') {
    discountTotal = discountTotalPoint * productPricing.flatRate;
  }

  // 不足分などのポイント計算
  const shortagePoint = totalPoint - discountTotalPoint - pointBalance;
  const orderTotalPoint = shortagePoint > 0 ? shortagePoint : 0;
  // 実通貨価格の計算
  const total = productPricing ? totalPoint * productPricing.flatRate : 0;

  // 使用ポイントの計算
  const useDepositTotalPoint = shortagePoint > 0 ? pointBalance : totalPoint - discountTotalPoint;
  const useDepositTotal = productPricing ? useDepositTotalPoint * productPricing.flatRate : 0;

  // 小計はクーポンとポイントで引かれた分を表示し、そこに対してtaxをかける
  const subtotal = total - discountTotal - useDepositTotal;
  const taxTotal = productPricing ? subtotal * productPricing.taxRate : 0;

  const orderTotal = productPricing ? orderTotalPoint * productPricing.flatRate * (1 + productPricing.taxRate) : 0;

  return (
    <Component
      pointBalance={pointBalance}
      isPointRequired={isPointRequired}
      productPricing={productPricing}
      className={className}
      total={total}
      discountCode={discountCode}
      discountTotal={discountTotal}
      useDepositTotal={useDepositTotal}
      useDepositTotalPoint={useDepositTotalPoint}
      subtotal={subtotal}
      taxTotal={taxTotal}
      orderTotal={orderTotal}
    />
  );
};

const Component: FC<Props> = ({
  pointBalance,
  isPointRequired,
  productPricing,
  total,
  className,
  discountCode,
  discountTotal,
  useDepositTotal,
  useDepositTotalPoint,
  subtotal,
  taxTotal,
  orderTotal,
}) => {
  return (
    <Root className={className}>
      <Table>
        <TableBody>
          <TableRow>
            <DenserTableCellWithoutBorderTitle align="right">
              <Title variant="subtitle1">
                <FormattedMessage id="required_point_summary.point_total" />
              </Title>
            </DenserTableCellWithoutBorderTitle>
            {isPointRequired && productPricing ? (
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint variant="subtitle1">
                    {productPricing.currency}
                    {(total || 0).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            ) : null}
          </TableRow>
          {discountCode && (
            <>
              <TableRow>
                <DenserTableCellWithoutBorder align="right">
                  <Title variant="subtitle1">
                    <FormattedMessage id="required_point_summary.discount_total" />
                  </Title>
                  {discountCode.name && <Typography>({displayMessageShorten(discountCode.name, 20)})</Typography>}
                </DenserTableCellWithoutBorder>
                {isPointRequired && productPricing ? (
                  <DenserTableCellWithoutBorder>
                    <AlignRight>
                      <RequiredPoint variant="subtitle1">
                        -{productPricing.currency}
                        {(discountTotal || 0).toLocaleString()}
                      </RequiredPoint>
                    </AlignRight>
                  </DenserTableCellWithoutBorder>
                ) : null}
              </TableRow>
            </>
          )}
          {isPointRequired && productPricing && pointBalance > 0 ? (
            // 残高が0の場合は表示しない
            <TableRow>
              <DenserTableCellWithoutBorderTitle align="right">
                <Title variant="subtitle1">
                  <FormattedMessage id="required_point_summary.deposits_total" />
                </Title>
              </DenserTableCellWithoutBorderTitle>
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint variant="subtitle1">
                    -{productPricing.currency}
                    {useDepositTotal.toLocaleString()}
                    <Point variant="caption">(-{useDepositTotalPoint.toLocaleString()}p)</Point>
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            </TableRow>
          ) : null}
          {(isPointRequired && productPricing && pointBalance > 0) || discountCode ? (
            <TableRow>
              <DenserTableCellWithoutBorder align="right">
                <Title variant="subtitle1">
                  <FormattedMessage id="required_point_summary.point_subtotal" />
                </Title>
              </DenserTableCellWithoutBorder>
              {isPointRequired && productPricing ? (
                <DenserTableCellWithoutBorder>
                  <AlignRight>
                    <RequiredPoint variant="subtitle1">
                      {productPricing.currency}
                      {(subtotal || 0).toLocaleString()}
                    </RequiredPoint>
                  </AlignRight>
                </DenserTableCellWithoutBorder>
              ) : null}
            </TableRow>
          ) : null}
          <TableRow>
            <DenserTableCellWithoutBorderTitle align="right">
              <Title variant="subtitle1">
                <FormattedMessage id="required_point_summary.tax_total" />
              </Title>
            </DenserTableCellWithoutBorderTitle>
            {isPointRequired && productPricing ? (
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint variant="subtitle1">
                    {productPricing.currency}
                    {(taxTotal || 0).toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            ) : null}
          </TableRow>
          <TableRow>
            <DenserTableCellWithoutBorderTitle align="right">
              <Title variant="h5">
                <FormattedMessage id="required_point_summary.order_total" />
              </Title>
            </DenserTableCellWithoutBorderTitle>
            {isPointRequired && productPricing ? (
              <DenserTableCellWithoutBorder>
                <AlignRight>
                  <RequiredPoint variant="h5" color="primary">
                    {productPricing.currency}
                    {orderTotal.toLocaleString()}
                  </RequiredPoint>
                </AlignRight>
              </DenserTableCellWithoutBorder>
            ) : null}
          </TableRow>
        </TableBody>
      </Table>
    </Root>
  );
};

const Root = styled('div')({});

const DenserTableCell = styled(TableCell)();
DenserTableCell.defaultProps = { size: 'small' };

const DenserTableCellWithoutBorder = styled(DenserTableCell)({
  borderBottom: 0,
  [mq('sp')]: {
    padding: '6px 24px 6px 0px',
  },
});

const DenserTableCellWithoutBorderTitle = styled(DenserTableCell)({
  borderBottom: 0,
  [mq('sp')]: {
    padding: '6px',
  },
});

const Title = styled(Typography)({});

const AlignRight = styled('div')({
  textAlign: 'right',
});

const RequiredPoint = styled(Typography)({
  fontWeight: 700,
});

const Point = styled(Typography)({
  fontWeight: 'bold',
});

export const CreationSetTotal = ContainerComponent;
